import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import CheckCircleIcon from "src/core/assets/images/icons/checkCircle.svg?react";
import CloseCircleIcon from "src/core/assets/images/icons/closeCircle.svg?react";
import EntrepriseIcon from "src/core/assets/images/icons/entreprise.svg?react";
import HomeIcon from "src/core/assets/images/icons/home.svg?react";
import IconModuler from "src/core/assets/images/icons/moduler.svg?react";
import PersonIcon from "src/core/assets/images/icons/person.svg?react";
import WarningIcon from "src/core/assets/images/icons/warningTransparent.svg?react";
import { EditContactModal } from "src/core/components/contact/EditContactModal";
import { ContactOwnerBlock } from "src/core/components/folder/header/sections/ContactOwnerBlock";
import { Status } from "src/core/constants/folders";
import { useFolderHeader } from "src/core/hooks/folder/header/useFolderHeader";
import { useRequiredTenantPaths } from "src/core/hooks/routing/useTenantPaths";
import { cn } from "src/core/lib/utils";
import { getRiskClass } from "src/core/utils/riskClass";

export const FolderHeaderEC = () => {
  const { t } = useTranslation();
  const paths = useRequiredTenantPaths();

  const { vm, api } = useFolderHeader();

  return (
    <div className="w-full min-w-[1280px] bg-gray-xxlight shadow-soft z-10 sticky top-0">
      <div className="flex flex-row w-full pl-20 py-5 items-center">
        <div className="flex flex-row h-full items-center w-[40%]">
          <Link
            className="flex size-30 items-center justify-center border border-border rounded-lg cursor-pointer text-foreground"
            to={paths.home()}
          >
            <HomeIcon data-testid="home-icon" />
          </Link>

          {vm.isPerson ? (
            <PersonIcon className="ml-30 h-20" />
          ) : (
            <EntrepriseIcon className="ml-30" />
          )}
          <span className="flex-1 ml-5 truncate">
            {vm.name.trim() !== "" ? vm.name : t("folder.noName")}
          </span>
          {vm.folderState === Status.VALIDATION_PENDING_HR && (
            <span className="flex items-center bg-red-medium font-light text-xs h-30 px-10 rounded-lg border border-red-medium text-gray-xlight mr-10">
              <WarningIcon className="size-20 mr-5 text-white" />
              {t("folder.header.highRisk")}
            </span>
          )}
          {vm.folderState === Status.REJECTED && (
            <span className="flex items-center bg-red-medium font-light h-30 px-10 text-xs rounded-lg border border-red-medium text-gray-xlight mr-10">
              <CloseCircleIcon className="size-20 mr-5" />
              {t("folder.header.refused")}
            </span>
          )}
          {vm.folderState === Status.VALIDATED && (
            <span className="flex items-center bg-blue-completed font-light h-30 px-10 text-xs rounded-lg border border-blue-completed text-gray-xlight mr-10">
              <CheckCircleIcon className="size-20 mr-5" />
              {t("folder.header.validated")}
            </span>
          )}
        </div>

        <div className="flex flex-row h-full items-center w-[60%] px-10">
          {vm.folderState === Status.DRAFT && (
            <span className="flex items-center h-30 px-10 border border-border rounded-lg text-gray-xlight font-light text-sm mr-[100px] bg-foreground">
              {t("folder.header.state.draft")}
            </span>
          )}

          {vm.folderState &&
            [Status.VALIDATION_PENDING, Status.VALIDATION_PENDING_HR].includes(vm.folderState) && (
              <span
                className={cn(
                  "flex items-center h-30 px-10 border border-border rounded-lg text-gray-xlight font-light text-sm mr-[100px]",
                  getRiskClass("dot", vm.globalRisk)
                )}
              >
                {t(`folder.cartographie.globalRisk.${vm.globalRisk.toLowerCase()}`)}
                &nbsp;:&nbsp;
                {t("folder.header.state.toValidate")}
              </span>
            )}

          {vm.folderState && [Status.VALIDATED, Status.REJECTED].includes(vm.folderState) && (
            <span
              className={cn(
                "flex items-center h-30 px-10 border border-border rounded-lg text-gray-xlight font-light text-sm mr-[100px]",
                getRiskClass("dot", vm.folderRisks.global)
              )}
            >
              <>
                {vm.isRiskForced && <IconModuler className="mr-10" />}
                {t("folder.header.riskLabel")}
                {t(
                  `folder.cartographie.globalRisk.${vm.folderRisks.global.toLowerCase()}`
                ).toLowerCase()}
              </>
            </span>
          )}

          <div className="flex flex-row ml-20">
            <ContactOwnerBlock
              contactPerson={vm.contactPerson}
              owner={vm.owner}
              isOwner={!!(vm.owner?.firstName || vm.owner?.lastName)}
              onEdit={() => api.setOpen(true)}
              canEdit={vm.canEditContact}
              canFollow={vm.canFollow}
            />
          </div>
        </div>

        {vm.customerFolderId && (
          <EditContactModal
            open={api.open}
            setOpen={api.setOpen}
            customerFolderId={vm.customerFolderId}
            contactPerson={vm.contactPerson}
            owner={vm.owner}
            onSuccess={api.refetch}
          />
        )}
      </div>
    </div>
  );
};
